import { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, TextField, MenuItem, Pagination, Skeleton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { accountsApi, Transaction } from '../api/accounts';

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  COMPLETED: 'success', FAILED: 'error', PENDING: 'warning', PROCESSING: 'info', REVERSED: 'default',
};

const typeOptions = ['', 'DEPOSIT', 'WITHDRAWAL', 'TRANSFER', 'PAYMENT', 'FEE', 'INTEREST', 'REVERSAL'];
const statusOptions = ['', 'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'REVERSED'];

export default function TransactionsPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const effectiveAccountId = accountId ?? 'all';

  const { data: res, isLoading } = useQuery({
    queryKey: ['transactions', effectiveAccountId, page, typeFilter, statusFilter],
    queryFn: () =>
      accountsApi.getTransactions(effectiveAccountId, {
        page, pageSize: 10,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
      }),
    enabled: !!effectiveAccountId,
  });

  const transactions: Transaction[] = res?.data?.data ?? [];
  const totalPages = res?.data?.totalPages ?? 1;

  const fmt = (amount: string, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' })
      .format(parseFloat(amount));

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Transactions</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField select label="Type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          size="small" sx={{ minWidth: 140 }}>
          <MenuItem value="">All Types</MenuItem>
          {typeOptions.filter(Boolean).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          size="small" sx={{ minWidth: 140 }}>
          <MenuItem value="">All Statuses</MenuItem>
          {statusOptions.filter(Boolean).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                </TableRow>
              ))
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No transactions found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell>{fmtDate(tx.initiatedAt)}</TableCell>
                  <TableCell>{tx.type}</TableCell>
                  <TableCell>{tx.description}</TableCell>
                  <TableCell align="right" sx={{
                    color: ['DEPOSIT', 'INTEREST'].includes(tx.type) ? 'success.main' : 'error.main',
                    fontWeight: 600,
                  }}>
                    {['DEPOSIT', 'INTEREST'].includes(tx.type) ? '+' : '-'}{fmt(tx.amount, tx.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip label={tx.status} size="small" color={statusColor[tx.status] ?? 'default'} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{tx.referenceNumber}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
}
